import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import {
  tmsApi,
  type AddOrderCheckpointRequest,
  type MarkOrderCheckpointDoneRequest,
  type OrderCheckpoints,
  type OrderDocument,
  type OrderDocumentTypeValue,
  type SkipOrderCheckpointRequest,
} from '../client';
import { orderDocumentKeys } from './useOrderDocuments';

/** Where the phone's files are stored — an order document pointing here is one of them, linked. */
const CHECKPOINT_CONTAINER = 'checkpoint-attachments';

/**
 * The order document a checkpoint file already is, if any. The document is
 * linked to the file's own blob (`{account}/{attachmentId}`, ids without
 * dashes), so that is how it is recognised — the BE finds it the same way.
 */
export function documentForCheckpointAttachment(
  documents: readonly OrderDocument[] | undefined,
  attachmentId: string,
): OrderDocument | undefined {
  const tail = `/${attachmentId.replace(/-/g, '').toLowerCase()}`;
  return documents?.find((d) => d.containerName === CHECKPOINT_CONTAINER && d.blobName.toLowerCase().endsWith(tail));
}

/**
 * Put a checkpoint photo or signature on the order's Documents as a type. The
 * Documents list is refetched, which is what the POD pill and the invoicing
 * chip read — a POD counts for its leg as soon as it lands.
 */
export function useAttachCheckpointAttachmentToOrder(organisationId: string | null, orderId: string | null) {
  const qc = useQueryClient();
  return useMutation<string, Error, { attachmentId: string; type: OrderDocumentTypeValue }>({
    mutationFn: ({ attachmentId, type }) => {
      if (!organisationId || !orderId) throw new Error('No order');
      return tmsApi.attachOrderCheckpointAttachment(orderId, attachmentId, { organisationId, type });
    },
    onSuccess: () => {
      if (orderId) qc.invalidateQueries({ queryKey: orderDocumentKeys.byOrder(orderId) });
    },
  });
}

export const orderCheckpointKeys = {
  all: ['tms', 'order-checkpoints'] as const,
  byOrder: (orderId: string) => ['tms', 'order-checkpoints', orderId] as const,
  attachment: (attachmentId: string) => ['tms', 'order-checkpoints', 'attachment', attachmentId] as const,
};

/**
 * A photo or signature from the phone as an object URL, for an <img> in the
 * page. Never opened as its own tab — a signature is an SVG, and an SVG opened
 * as a page runs its scripts (the same rule as myCheck's evidence).
 */
export function useOrderCheckpointAttachment(
  organisationId: string | null,
  orderId: string | null,
  attachmentId: string | null,
) {
  const query = useQuery<Blob, Error>({
    queryKey: attachmentId ? orderCheckpointKeys.attachment(attachmentId) : orderCheckpointKeys.all,
    queryFn: () => tmsApi.getOrderCheckpointAttachment(organisationId!, orderId!, attachmentId!),
    enabled: !!organisationId && !!orderId && !!attachmentId,
    // Evidence never changes once recorded.
    staleTime: Infinity,
  });
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!query.data) return;
    const next = URL.createObjectURL(query.data);
    setUrl(next);
    return () => URL.revokeObjectURL(next);
  }, [query.data]);
  return { url, isLoading: query.isPending, isError: query.isError };
}

/** An order's assembled checkpoints with what has been done. `null` keeps it disabled. */
export function useOrderCheckpoints(organisationId: string | null, orderId: string | null) {
  return useQuery<OrderCheckpoints, Error>({
    queryKey: orderId ? orderCheckpointKeys.byOrder(orderId) : orderCheckpointKeys.all,
    queryFn: () => {
      if (!organisationId || !orderId) throw new Error('No order');
      return tmsApi.getOrderCheckpoints(organisationId, orderId);
    },
    enabled: !!organisationId && !!orderId,
    staleTime: 30 * 1000,
  });
}

/**
 * The actions on an order's checkpoints. Each answers with the refreshed list,
 * which replaces the cache directly — no second round-trip.
 */
export function useOrderCheckpointActions(organisationId: string | null, orderId: string | null) {
  const qc = useQueryClient();
  const settle = (next: OrderCheckpoints) => {
    if (orderId) qc.setQueryData(orderCheckpointKeys.byOrder(orderId), next);
  };
  const need = () => {
    if (!organisationId || !orderId) throw new Error('No order');
    return { organisationId, orderId };
  };

  const done = useMutation<OrderCheckpoints, Error, Omit<MarkOrderCheckpointDoneRequest, 'organisationId'>>({
    mutationFn: (body) => {
      const { organisationId: org, orderId: id } = need();
      return tmsApi.markOrderCheckpointDone(id, { ...body, organisationId: org });
    },
    onSuccess: settle,
  });
  const skip = useMutation<OrderCheckpoints, Error, Omit<SkipOrderCheckpointRequest, 'organisationId'>>({
    mutationFn: (body) => {
      const { organisationId: org, orderId: id } = need();
      return tmsApi.skipOrderCheckpoint(id, { ...body, organisationId: org });
    },
    onSuccess: settle,
  });
  const undo = useMutation<OrderCheckpoints, Error, string>({
    mutationFn: (recordId) => {
      const { organisationId: org, orderId: id } = need();
      return tmsApi.undoOrderCheckpointRecord(org, id, recordId);
    },
    onSuccess: settle,
  });
  const add = useMutation<OrderCheckpoints, Error, Omit<AddOrderCheckpointRequest, 'organisationId'>>({
    mutationFn: (body) => {
      const { organisationId: org, orderId: id } = need();
      return tmsApi.addOrderCheckpoint(id, { ...body, organisationId: org });
    },
    onSuccess: settle,
  });
  const remove = useMutation<OrderCheckpoints, Error, string>({
    mutationFn: (adhocId) => {
      const { organisationId: org, orderId: id } = need();
      return tmsApi.removeOrderCheckpoint(org, id, adhocId);
    },
    onSuccess: settle,
  });

  return {
    done,
    skip,
    undo,
    add,
    remove,
    busy: done.isPending || skip.isPending || undo.isPending || add.isPending || remove.isPending,
  };
}
